import { useState, useCallback } from "react";
import { parseLink } from "./contractParser.js";
import { resolveRegionId, findPublicContract, resolveStationSystem, mapContractType } from "./esiContracts.js";

/* ── API Endpoints ── */
const ESI = "https://esi.evetech.net/latest";
const ZKB = "https://zkillboard.com/api";
const ZKB_WINDOW_SECONDS = 1200;

/* ── Notorious systems (hardcoded danger flags) ── */
const NOTORIOUS = {
  30002718: "High-traffic ganking choke (Uedama)",
  30002797: "Notorious gate camp (Rancer)",
  30045342: "Pirate PvP hub (Tama)",
  30002537: "Pirate PvP hub (Amamake)",
  30003088: "Low-sec PvP hub (Old Man Star)",
  30001984: "Ganking activity (Hek approach)",
};

/* ── Security utilities ── */
function secColor(v) {
  if (v >= 0.9) return "#2CE8D0";
  if (v >= 0.8) return "#36E890";
  if (v >= 0.7) return "#66E830";
  if (v >= 0.6) return "#B8E820";
  if (v >= 0.5) return "#E8E020";
  if (v >= 0.4) return "#E89820";
  if (v >= 0.3) return "#E86820";
  if (v >= 0.2) return "#E84820";
  if (v > 0.0) return "#E82820";
  return "#E80808";
}
function secType(v) {
  if (v >= 0.5) return "hs";
  if (v > 0.0) return "ls";
  return "ns";
}
function secDisp(v) {
  // Floor toward zero for display (matches EVE client behavior)
  if (v >= 0) return Math.max(0, Math.floor(v * 10) / 10).toFixed(1);
  return (Math.ceil(v * 10) / 10).toFixed(1);
}
function secLabel(v) {
  return secType(v) === "hs" ? "Highsec" : secType(v) === "ls" ? "Lowsec" : "Nullsec";
}
function secBadgeColor(v) {
  return secType(v) === "hs" ? "#24B034" : secType(v) === "ls" ? "#E87820" : "#E82020";
}

/* ── Number formatters ── */
function fmtVol(v) {
  return v == null ? "-" : v.toLocaleString(undefined, { maximumFractionDigits: 2 }) + " m³";
}
function fmtISK(n) {
  if (!n && n !== 0) return "-";
  if (n >= 1e12) return (n / 1e12).toFixed(2) + " T";
  if (n >= 1e9) return (n / 1e9).toFixed(2) + " B";
  if (n >= 1e6) return (n / 1e6).toFixed(2) + " M";
  return Math.round(n).toLocaleString();
}
function fmtISKOrDash(n) {
  return !n ? "-" : fmtISK(n);
}

/* ── Design tokens ── */
const BG = "#07090e";
const CARD = "#0b1020";
const BORDER = "#192838";
const TEXT = "#8aaec8";
const DIM = "#6088ac";
const ACCENT = "#42b4d6";
const FONT = "ui-monospace,'Cascadia Code','SF Mono','Courier New',monospace";

/* ── Micro-components ── */
function Panel({ children, style }) {
  return (
    <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 3, overflow: "hidden", ...style }}>
      {children}
    </div>
  );
}
function PH({ children, right }) {
  return (
    <div style={{ padding: "7px 14px", borderBottom: `1px solid ${BORDER}`, color: DIM, fontSize: 10, letterSpacing: "0.14em", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span>{children}</span>
      {right && <span style={{ color: DIM, letterSpacing: "0.05em" }}>{right}</span>}
    </div>
  );
}
function StatCard({ label, value, color, sub }) {
  return (
    <Panel>
      <div style={{ padding: "14px 10px", textAlign: "center" }}>
        <div style={{ color: DIM, fontSize: 10, letterSpacing: "0.12em", marginBottom: 5 }}>{label}</div>
        <div style={{ color: color ?? ACCENT, fontSize: 26, fontWeight: "bold", lineHeight: 1 }}>{value}</div>
        {sub && <div style={{ color: DIM, fontSize: 10, marginTop: 3 }}>{sub}</div>}
      </div>
    </Panel>
  );
}

/* ── Main App ── */
export default function App() {
  const [input, setInput] = useState(
    ''
  );
  const [contract, setContract] = useState(null);
  const [route, setRoute] = useState(null);      // array of system IDs
  const [sys, setSys] = useState({});            // systemId → ESI system obj
  const [zkb, setZkb] = useState({});           // systemId → zkb stats obj
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("");
  const [err, setErr] = useState(null);
  const [zkbOk, setZkbOk] = useState(null);     // null=pending, true/false
  const [verified, setVerified] = useState(false);

  const analyze = useCallback(async () => {
    setErr(null); setContract(null); setRoute(null); setSys({}); setZkb({}); setZkbOk(null); setVerified(false);
    setLoading(true);
    try {
      setStep("Parsing contract link…");
      const c = parseLink(input);
      if (!c) throw new Error(
        "Could not parse. Paste the full <url=contract:…> link, or plain 'From >> To (vol m³) (Type)' text."
      );
      setContract(c);

      let routeSrc = c.src;
      let routeDst = c.dst;

      if (c.contractId) {
        setStep("Resolving contract region…");
        const regionId = await resolveRegionId(ESI, c.sysId);

        setStep("Searching public contracts in region…");
        const found = await findPublicContract(ESI, regionId, c.contractId);
        if (!found) {
          throw new Error(
            "Contract not found in this region's public listings - it's likely private/personal, already accepted, or expired. Only public contracts can be ISK-verified via ESI."
          );
        }
        if (found.type !== "courier") {
          throw new Error(
            `This is a ${mapContractType(found.type)} contract - only courier contracts can be analyzed.`
          );
        }

        const verifiedFields = {
          price: found.price ?? 0,
          reward: found.reward ?? 0,
          collateral: found.collateral ?? 0,
          vol: found.volume,
          type: mapContractType(found.type),
        };
        setVerified(true);

        if (found.type === "courier" && found.start_location_id && found.end_location_id) {
          const [startSys, endSys] = await Promise.all([
            resolveStationSystem(ESI, found.start_location_id),
            resolveStationSystem(ESI, found.end_location_id),
          ]);
          if (startSys && endSys) {
            routeSrc = { name: startSys.name, id: startSys.systemId };
            routeDst = { name: endSys.name, id: endSys.systemId };
          }
        }

        setContract((prev) => ({
          ...prev,
          ...verifiedFields,
          src: routeSrc && typeof routeSrc === "object" ? routeSrc.name : prev.src,
          dst: routeDst && typeof routeDst === "object" ? routeDst.name : prev.dst,
        }));
      }

      if (!routeSrc || !routeDst) {
        // No route to analyze (e.g. item-exchange contract, or a courier
        // whose text didn't parse and whose locations are unresolvable
        // structures). The existing `finally` block below still resets
        // loading/step - just stop here.
        return;
      }

      let srcId, dstId;
      if (typeof routeSrc === "object" && typeof routeDst === "object") {
        srcId = routeSrc.id;
        dstId = routeDst.id;
      } else {
        setStep(`Resolving "${routeSrc}" and "${routeDst}" via ESI…`);
        const ir = await fetch(`${ESI}/universe/ids/?datasource=tranquility&language=en`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify([routeSrc, routeDst]),
        });
        if (!ir.ok) throw new Error(`ESI name resolution failed (HTTP ${ir.status})`);
        const id = await ir.json();
        const nm = Object.fromEntries((id.systems ?? []).map((s) => [s.name.toLowerCase(), s.id]));
        srcId = nm[routeSrc.toLowerCase()];
        dstId = nm[routeDst.toLowerCase()];
        if (!srcId) throw new Error(`System not found: "${routeSrc}". Check spelling or try the system name if a station name was used.`);
        if (!dstId) throw new Error(`System not found: "${routeDst}". Check spelling or try the system name if a station name was used.`);
      }

      setStep("Calculating shortest route…");
      const rr = await fetch(`${ESI}/route/${srcId}/${dstId}/?datasource=tranquility&flag=shortest`);
      if (!rr.ok) throw new Error("Route calculation failed - systems may be unreachable from each other");
      const rt = await rr.json();
      if (!Array.isArray(rt) || rt.length === 0) throw new Error("No route found between these systems");
      setRoute(rt);

      setStep(`Loading ${rt.length} system details…`);
      const sr = await Promise.all(
        rt.map(id =>
          fetch(`${ESI}/universe/systems/${id}/?datasource=tranquility&language=en`)
            .then(r => r.ok ? r.json() : null)
            .catch(() => null)
        )
      );
      const sm = {};
      sr.forEach(s => { if (s) sm[s.system_id] = s; });
      setSys(sm);

      setStep("Fetching zKillboard recent activity…");
      const zr = await Promise.allSettled(
        rt.map(id =>
          fetch(`${ZKB}/kills/systemID/${id}/pastSeconds/${ZKB_WINDOW_SECONDS}/`)
            .then(r => r.ok ? r.json() : null)
            .then(d => Array.isArray(d) ? { id, kills: d.filter(Boolean) } : null)
            .catch(() => null)
        )
      );
      const zm = {}; let zh = 0;
      zr.forEach(r => {
        if (r.status === "fulfilled" && r.value) {
          const { id, kills } = r.value;
          const isk = kills.reduce((sum, k) => sum + (k?.zkb?.totalValue ?? 0), 0);
          zm[id] = { kills: kills.length, isk };
          zh++;
        }
      });
      setZkb(zm);
      setZkbOk(zh > 0);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
      setStep("");
    }
  }, [input]);

  /* ── Derive route stats ── */
  const stats = (() => {
    if (!route || route.length === 0) return null;
    let hs = 0, ls = 0, ns = 0, minSec = 1.1, kills = 0, isk = 0;
    const notori = [];
    for (const id of route) {
      const s = sys[id];
      const sec = s?.security_status ?? 1.0;
      const t = secType(sec);
      if (t === "hs") hs++; else if (t === "ls") ls++; else ns++;
      if (sec < minSec) minSec = sec;
      if (NOTORIOUS[id]) notori.push({ id, name: s?.name ?? String(id), note: NOTORIOUS[id] });
      const zd = zkb[id];
      if (zd) { kills += zd.kills ?? 0; isk += zd.isk ?? 0; }
    }
    const jumps = route.length - 1;
    const danger = Math.min(100, ls * 5 + ns * 10 + notori.length * 25);
    return { jumps, hs, ls, ns, minSec, notori, kills, isk, danger };
  })();

  const dangerInfo = stats ? (
    stats.danger >= 70 ? { text: "VERY DANGEROUS", color: "#E02020" } :
    stats.danger >= 40 ? { text: "DANGEROUS",      color: "#E07020" } :
    stats.danger >= 15 ? { text: "ELEVATED RISK",  color: "#E0C020" } :
    { text: "RELATIVELY SAFE", color: "#24B034" }
  ) : null;

  const hasZ = Object.keys(zkb).length > 0;

  return (
    <div style={{ minHeight: "100vh", background: BG, color: TEXT, fontFamily: FONT }}>
      {/* ── Header bar ── */}
      <div style={{
        background: "linear-gradient(90deg, #060c1a 0%, #09152a 50%, #060c1a 100%)",
        borderBottom: `1px solid ${BORDER}`,
        padding: "11px 20px",
        display: "flex",
        alignItems: "center",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth="1.5">
            <polygon points="12 2 15 9 22 9 17 14 19 21 12 17 5 21 7 14 2 9 9 9" />
          </svg>
          <span style={{ color: ACCENT, fontSize: 13, fontWeight: "bold", letterSpacing: "0.18em" }}>
            EVE CONTRACT ROUTE ANALYZER
          </span>
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ color: DIM, fontSize: 11, letterSpacing: "0.06em" }}>ESI · zKillboard</span>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "16px 14px" }}>

        {/* ── Input ── */}
        <Panel style={{ marginBottom: 12 }}>
          <PH>CONTRACT LINK</PH>
          <div style={{ padding: 12 }}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              rows={2}
              spellCheck={false}
              placeholder="Paste <url=contract:…> or plain 'System A >> System B (vol m³) (Type)'"
              style={{
                width: "100%", boxSizing: "border-box",
                background: "#04070e",
                border: `1px solid ${BORDER}`,
                borderRadius: 2,
                color: "#6898b8",
                fontFamily: FONT,
                fontSize: 12,
                padding: "8px 10px",
                resize: "vertical",
                outline: "none",
                minHeight: 50,
                lineHeight: 1.6,
              }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
              <span style={{ color: DIM, fontSize: 11 }}>
                Accepts &lt;url=contract:…&gt; tags or plain "From &gt;&gt; To" text
              </span>
              <button
                onClick={analyze}
                disabled={loading || !input.trim()}
                style={{
                  padding: "6px 18px",
                  background: loading ? "#080e1c" : "#0a1c34",
                  border: `1px solid ${loading ? BORDER : "#1c4890"}`,
                  borderRadius: 2,
                  color: loading ? DIM : ACCENT,
                  fontFamily: FONT,
                  fontSize: 12,
                  letterSpacing: "0.1em",
                  cursor: loading ? "not-allowed" : "pointer",
                  fontWeight: "bold",
                  transition: "border-color 0.15s",
                }}
              >
                {loading ? "⟳ ANALYZING…" : "ANALYZE ROUTE"}
              </button>
            </div>
          </div>
        </Panel>

        {/* ── Loading status ── */}
        {loading && step && (
          <div style={{ color: "#3ecf8e", fontSize: 11, letterSpacing: "0.06em", marginBottom: 10 }}>
            ⟳ {step}
          </div>
        )}

        {/* ── Error ── */}
        {err && (
          <div style={{
            background: "#120505",
            border: "1px solid #480808",
            borderRadius: 3,
            padding: "8px 12px",
            color: "#E05050",
            fontSize: 12,
            marginBottom: 12,
          }}>
            ⚠ {err}
          </div>
        )}

        {/* ── Contract summary ── */}
        {contract && (
          <Panel style={{ marginBottom: 10 }}>
            <PH right={verified ? <span style={{ color: ACCENT }}>✓ ESI-VERIFIED</span> : undefined}>CONTRACT</PH>
            <div style={{ padding: "10px 14px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                {[{ label: "FROM", val: contract.src ?? "-" }, { label: "TO", val: contract.dst ?? "-" }].map(({ label, val }) => (
                  <div key={label}>
                    <div style={{ color: DIM, fontSize: 10, letterSpacing: "0.12em", marginBottom: 3 }}>{label}</div>
                    <div style={{ color: ACCENT, fontSize: 15, fontWeight: "bold" }}>{val}</div>
                  </div>
                ))}
              </div>
              <div style={{
                display: "grid",
                gridTemplateColumns: verified ? "1fr 1fr 1fr" : "1fr",
                gap: 14,
                alignItems: "center",
                borderTop: `1px solid ${BORDER}`,
                marginTop: 12,
                paddingTop: 10,
              }}>
                <div>
                  <div style={{ color: DIM, fontSize: 10, letterSpacing: "0.12em", marginBottom: 3 }}>VOLUME</div>
                  <div style={{ color: "#bca040", fontSize: 13 }}>{fmtVol(contract.vol)}</div>
                </div>
                {verified && (
                  <div>
                    <div style={{ color: DIM, fontSize: 10, letterSpacing: "0.12em", marginBottom: 3 }}>REWARD</div>
                    <div style={{ color: "#bca040", fontSize: 13 }}>{fmtISKOrDash(contract.reward)}</div>
                  </div>
                )}
                {verified && (
                  <div>
                    <div style={{ color: DIM, fontSize: 10, letterSpacing: "0.12em", marginBottom: 3 }}>COLLATERAL</div>
                    <div style={{ color: "#bca040", fontSize: 13 }}>{fmtISKOrDash(contract.collateral)}</div>
                  </div>
                )}
              </div>
            </div>
          </Panel>
        )}

        {/* ── Stats ── */}
        {stats && (<>

          {/* Stat cards row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 9, marginBottom: 9 }}>
            <StatCard label="TOTAL JUMPS" value={stats.jumps} />
            <StatCard label="HIGHSEC"     value={stats.hs}    color="#24B034" sub="systems" />
            <StatCard label="LOWSEC"      value={stats.ls}    color="#E87820" sub="systems" />
            <StatCard label="NULLSEC"     value={stats.ns}    color="#E82020" sub="systems" />
          </div>

          {/* Danger + Security row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9, marginBottom: 9 }}>

            <Panel>
              <PH>DANGER ASSESSMENT</PH>
              <div style={{ padding: "12px 14px" }}>
                <div style={{ color: dangerInfo.color, fontSize: 17, fontWeight: "bold", marginBottom: 9 }}>
                  {dangerInfo.text}
                </div>
                <div style={{ height: 3, background: "#050810", borderRadius: 2, marginBottom: 11 }}>
                  <div style={{ height: "100%", width: `${stats.danger}%`, background: dangerInfo.color, borderRadius: 2 }} />
                </div>
                {stats.notori.length > 0
                  ? stats.notori.map(n => (
                    <div key={n.id} style={{ color: "#E04444", fontSize: 11, marginTop: 5, lineHeight: 1.4 }}>
                      ⚠ <strong style={{ color: "#F06868" }}>{n.name}</strong> - {n.note}
                    </div>
                  ))
                  : <div style={{ color: DIM, fontSize: 11 }}>No notorious waypoints detected</div>
                }
              </div>
            </Panel>

            <Panel>
              <PH>ROUTE SECURITY</PH>
              <div style={{ padding: "12px 14px" }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
                  <span style={{ color: DIM, fontSize: 11 }}>Minimum status:</span>
                  <span style={{ color: secColor(stats.minSec), fontSize: 22, fontWeight: "bold" }}>
                    {secDisp(stats.minSec)}
                  </span>
                  <span style={{ color: secBadgeColor(stats.minSec), fontSize: 11 }}>
                    {secLabel(stats.minSec)}
                  </span>
                </div>
                {hasZ ? (
                  <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 10 }}>
                    <div style={{ color: DIM, fontSize: 10, letterSpacing: "0.1em", marginBottom: 7 }}>
                      ROUTE KILLS - LAST 30 MINUTES
                    </div>
                    <div style={{ display: "flex", gap: 24 }}>
                      <div>
                        <div style={{ color: DIM, fontSize: 10 }}>Kills</div>
                        <div style={{ color: "#E07020", fontSize: 16, fontWeight: "bold" }}>
                          {stats.kills.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: DIM, fontSize: 10 }}>ISK Destroyed</div>
                        <div style={{ color: "#E05020", fontSize: 16, fontWeight: "bold" }}>
                          {fmtISK(stats.isk)}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : zkbOk === false ? (
                  <div style={{ color: DIM, fontSize: 11, fontStyle: "italic" }}>
                    zKillboard data unavailable
                  </div>
                ) : null}
              </div>
            </Panel>
          </div>

          {/* Route map */}
          <Panel style={{ marginBottom: 9 }}>
            <PH right={`${route.length} systems · ${stats.jumps} jumps`}>ROUTE MAP</PH>
            <div style={{ padding: "12px 14px" }}>
              <div style={{ overflowX: "auto", paddingBottom: 4 }}>
                <div style={{ display: "flex", gap: 2, minWidth: "fit-content", alignItems: "flex-end" }}>
                  {route.map((id, i) => {
                    const s = sys[id];
                    const sec = s?.security_status ?? 1.0;
                    const isEnd = i === 0 || i === route.length - 1;
                    const isN = !!NOTORIOUS[id];
                    const bw = route.length > 55 ? 6 : route.length > 38 ? 9 : 15;
                    const bh = route.length > 55 ? 16 : route.length > 38 ? 22 : 30;
                    return (
                      <div key={id} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <div style={{ color: isN ? "#D02020" : "transparent", fontSize: 7, lineHeight: 1, marginBottom: 2 }}>
                          ▲
                        </div>
                        <div
                          title={`${s?.name ?? id} · ${secDisp(sec)} (${secLabel(sec)})`}
                          style={{
                            width: bw,
                            height: bh,
                            background: secColor(sec),
                            opacity: 0.88,
                            border: isEnd ? `2px solid ${ACCENT}` : "1px solid transparent",
                            boxSizing: "border-box",
                            borderRadius: 1,
                            cursor: "default",
                          }}
                        />
                        {isEnd && (
                          <div style={{ fontSize: 8, color: ACCENT, marginTop: 3, whiteSpace: "nowrap" }}>
                            {s?.name?.slice(0, 9) ?? ""}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Legend */}
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 10 }}>
                {[
                  ["#66E830", "Highsec ≥ 0.5"],
                  ["#E87020", "Lowsec 0.1–0.4"],
                  ["#E80808", "Nullsec ≤ 0.0"],
                  [ACCENT,    "Origin / Destination"],
                  ["#D02020", "▲ Notable system"],
                ].map(([color, label]) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <div style={{ width: 9, height: 9, background: color, borderRadius: 1, flexShrink: 0 }} />
                    <span style={{ fontSize: 10, color: DIM }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </Panel>

          {/* System table */}
          <Panel>
            <PH>ROUTE DETAILS</PH>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#050a16" }}>
                    {[
                      { h: "#",        align: "right"  },
                      { h: "SYSTEM",   align: "left"   },
                      { h: "SEC",      align: "left"   },
                      { h: "CLASS",    align: "left"   },
                      ...(hasZ ? [
                        { h: "KILLS (30min)", align: "right" },
                        { h: "ISK DEST.",   align: "right" },
                      ] : []),
                      { h: "NOTES",    align: "left"   },
                    ].map(({ h, align }) => (
                      <th key={h} style={{ padding: "6px 12px", color: DIM, fontSize: 10, letterSpacing: "0.1em", textAlign: align, fontWeight: "normal", borderBottom: `1px solid ${BORDER}` }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {route.map((id, i) => {
                    const s = sys[id];
                    const sec = s?.security_status ?? 1.0;
                    const zd = zkb[id];
                    const killCt = zd ? zd.kills : null;
                    const iskVal = zd ? zd.isk : null;
                    const nNote = NOTORIOUS[id];
                    const isEnd = i === 0 || i === route.length - 1;
                    const t = secType(sec);
                    return (
                      <tr key={id} style={{
                        background: isEnd ? "#08111e" : "transparent",
                        borderBottom: `1px solid #0b1620`,
                      }}>
                        <td style={{ padding: "5px 12px", color: DIM, textAlign: "right" }}>{i}</td>
                        <td style={{ padding: "5px 12px", color: isEnd ? ACCENT : TEXT, fontWeight: isEnd ? "bold" : "normal" }}>
                          <a
                            href={`https://zkillboard.com/system/${id}/`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: "inherit", textDecoration: "none" }}
                            title="View on zKillboard"
                          >
                            {s?.name ?? id}
                          </a>
                          {isEnd && (
                            <span style={{ color: DIM, fontSize: 10, marginLeft: 6 }}>
                              {i === 0 ? "(origin)" : "(destination)"}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "5px 12px", color: secColor(sec), fontWeight: "bold" }}>
                          {secDisp(sec)}
                        </td>
                        <td style={{ padding: "5px 12px", fontSize: 10, color: secBadgeColor(sec) }}>
                          {t === "hs" ? "HIGHSEC" : t === "ls" ? "LOWSEC" : "NULLSEC"}
                        </td>
                        {hasZ && (
                          <td style={{ padding: "5px 12px", textAlign: "right", color: killCt > 10 ? "#E04020" : killCt > 3 ? "#E07020" : TEXT }}>
                            {killCt != null ? killCt.toLocaleString() : "-"}
                          </td>
                        )}
                        {hasZ && (
                          <td style={{ padding: "5px 12px", textAlign: "right", color: TEXT }}>
                            {iskVal != null ? fmtISK(iskVal) : "-"}
                          </td>
                        )}
                        <td style={{ padding: "5px 12px", color: "#E04444", fontSize: 11 }}>
                          {nNote && `⚠ ${nNote}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Panel>

          {zkbOk === false && (
            <div style={{ color: DIM, fontSize: 11, textAlign: "center", marginTop: 10 }}>
              zKillboard data unavailable (CORS or rate limit) - danger score derived from security status only
            </div>
          )}

        </>)}
      </div>
    </div>
  );
}
